/*
 * <nc-looks> — "Mis looks": the session gallery of generated looks, the look viewer and the floating "Creando tu look" chip (Tanda A of
 * the probador, docs/normcore-probador-ux-leader.md §4 and §6: A3 viewer, A4 gallery, A5 non-blocking wait). Phase A1, no backend.
 *
 * Data: only through NcApp.gateway(): listGeneratedLooks / saveGeneratedLook / removeGeneratedLook (the mock keeps them per user with the
 * favorites, localStorage in A1; DP-8 30 days is Fase B), getAsset (the generated image and the garment photos), getLookRender (a look that
 * was still being created when the page was left is followed again, or marked as interrupted). Nothing is invented: a look without a
 * generated image shows its garments and says why ("Modo demostración" in the mock), never a fake picture.
 *
 * Who creates the looks: the host. <nc-fitting-room looks="on"> runs the render flow (photo, consent, quota, polling) and calls
 * add(record) / update(id, patch) / remove(id). This element only shows and keeps them:
 *   - gallery grid ([data-looks-grid]): every look is a persistent card (generating / ready / error), newest first; a new look never erases the
 *     previous one (DP-9: the gallery is the compare). An error card keeps its size and offers "Reintentar".
 *   - viewer ([data-looks-viewer]): the look large on the neutral stage, "Imagen generada con IA" only over a generated image, the garments with
 *     image, brand, title (link to the Normcore product page, DP-7 a), price and "Ir a la tienda" (the brand's own store when the record knows it:
 *     officialUrl of the product, else of the brand; otherwise the Normcore product page with its own label; new tab, rel="noopener",
 *     external icon), the total and the number of stores, and Editar / Guardar (the one heart) / Compartir (hidden while PENDING hides share).
 *     Pager 01 / 02 / 03 in creation order: thumbnails on the stage's right edge when wide, arrows + counter when narrow, keyboard ↑ / ↓
 *     (Home / End), vertical swipe on touch. Wide hosts give an inline place (setInlineHost: the fitting room's stage, used from 1000 px of the
 *     component = a 1064 px window: the two-column desktop of §4.2, N2 of docs/normcore-probador-revalidacion.md); otherwise the viewer opens in
 *     an nc-sheet (full screen on mobile; Escape / close return the focus to the control that opened it) and marks the room box
 *     `data-looks-modal` so the look panel behind it is not painted.
 *   - stage preview (setPreviewHost, N1 / N2 of the re-validation): below the two-column desktop the host's stage (compact row on mobile, the
 *     large stage on tablet) shows the newest look: the creating figure with the live time copy while it is being created, then its picture,
 *     which is the ONE entry to the viewer sheet. The host keeps the copy of its empty and ready states.
 *   - B2 aviso: the chip has a bell (while creating) that turns on a browser notification for when the look is ready and the tab is in the background
 *     (opt-in, the browser asks permission there; remembered in localStorage 'nc-looks-notify'). It only reaches a page that is still open: email or push
 *     for a closed tab needs a provider that does not exist yet (decision pending).
 *   - chip ([data-looks-chip], moved to <body>): the live time copy ("Creando tu look · unos 20 s" → "casi listo…" → the slow text) while a look
 *     is being created (48 px, bottom right, aria-live polite; below the two-column desktop a tap brings the look's gallery card into view),
 *     then "Tu look está listo · Ver" (or the error), which opens the viewer on that look. A ready look already on screen in the stage gets no
 *     chip (one entry to the viewer per look). The person keeps browsing meanwhile.
 *   - creating wait (§4.1 step 4, §4.4; Javi 2026-10-25 "muy estático"): stage, card and thumbnail show the garments of the look appearing one
 *     by one on a neutral figure at their body slots (waitPlan / waitStack, a 4 s CSS loop in phase with the elapsed time; static with reduced
 *     motion), "Creando tu look" + "unos 20 s" → "casi listo…" (20 s) → "Está tardando más de lo normal, seguimos en ello" (35 s), one polite
 *     announcement per change, never a percentage. Creating → ready cross-fades the stage out over the result (180 ms; 80 ms reduced motion).
 * Events (bubbling): nc-looks:edit {id, productIds}, nc-looks:retry {id, productIds}, nc-looks:change {count, generating}, nc-looks:open {id},
 * nc-looks:first (the «Crea tu primer look» CTA of the empty gallery, N11).
 * Copy: data-msg-* lists written by snippets/nc-looks.liquid from the host's section settings (Spanish defaults there and below).
 * Pure helpers are exported for node:test.
 */
(function () {
  var Core = typeof module !== 'undefined' && module.exports ? require('./nc-fitting-room.js') : (typeof window !== 'undefined' ? window.NcFittingRoomCore : null);

  function pad2(n) { var v = Math.max(0, Math.floor(n) || 0); return v < 10 ? '0' + v : String(v); }

  // creation order (oldest first): the pager numbers are stable, 01 is the first look of the gallery
  function chrono(list) {
    return (list || []).map(function (r, i) { return { r: r, i: i }; }).sort(function (a, b) {
      return ((a.r.createdAt || 0) - (b.r.createdAt || 0)) || (b.i - a.i);
    }).map(function (x) { return x.r; });
  }

  function lookNumber(list, id) { var at = chrono(list).map(function (r) { return r.id; }).indexOf(id); return at < 0 ? '' : pad2(at + 1); }

  // links that may go into an href: http(s) or a path of this store
  function safeUrl(v) { return typeof v === 'string' && /^(https?:\/\/|\/(?!\/))/i.test(v) ? v : ''; }

  // DP-7: "Ir a la tienda" goes to the brand's own store when it is known (product officialUrl, else the brand's), else to the Normcore page
  function shopLink(item) {
    var own = safeUrl(item && item.officialUrl) || safeUrl(item && item.brandOfficialUrl);
    if (own) return { href: own, kind: 'brand' };
    var page = safeUrl(item && item.url);
    return page ? { href: page, kind: 'normcore' } : { href: '', kind: '' };
  }

  // how many stores the look is bought in: one per brand (a garment without a brand counts as Normcore's catalogue)
  function storeCount(items) {
    var seen = {};
    (items || []).forEach(function (i) { seen[String((i && (i.brandSlug || i.brand)) || 'normcore').toLowerCase()] = true; });
    return Object.keys(seen).length;
  }

  function lookTotal(items) {
    if (!Core) return null;
    return Core.totalPrice((items || []).map(function (i) { return i && i.price; }));
  }

  function sameSet(a, b) {
    var x = (a || []).slice().sort();
    var y = (b || []).slice().sort();
    if (x.length !== y.length || !x.length) return false;
    for (var i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
    return true;
  }

  // next position of the pager (no wrap: the first and the last look are the ends, like Home / End)
  function stepIndex(i, delta, len) { if (!len) return -1; return Math.max(0, Math.min(len - 1, (i < 0 ? 0 : i) + delta)); }

  // a vertical swipe: up = next look (+1), down = previous (-1); short or mostly horizontal moves do nothing
  function swipeStep(dx, dy, min) {
    var m = min || 48;
    if (Math.abs(dy) < m || Math.abs(dy) < Math.abs(dx) * 1.2) return 0;
    return dy < 0 ? 1 : -1;
  }

  // zoom on the generated image (2026-10-30, option 1 of the "potenciar el probador" study: the look can be looked at close, like a shop
  // viewer): the picture scales about a point and pans inside its frame. s = 1 is the whole look, up to ZOOM_MAX; x / y are px from the
  // frame's centre. Every state goes through zoomClamp, so the image never shows the frame behind it.
  var ZOOM_MAX = 4;
  var ZOOM_STEP = 2;
  function zoomClamp(z, box) {
    var s = Math.min(ZOOM_MAX, Math.max(1, +z.s || 1));
    var w = (box && box.w) || 0, h = (box && box.h) || 0;
    var mx = (s - 1) * w / 2, my = (s - 1) * h / 2;
    var x = Math.min(mx, Math.max(-mx, +z.x || 0)), y = Math.min(my, Math.max(-my, +z.y || 0));
    return { s: s, x: x || 0, y: y || 0 }; // `|| 0` drops a -0
  }
  // scale by `k` about a point (px from the frame's centre): what is under the finger / cursor stays where it is
  function zoomAt(z, k, px, py, box) {
    var s = Math.min(ZOOM_MAX, Math.max(1, (+z.s || 1) * k));
    var r = s / (+z.s || 1);
    return zoomClamp({ s: s, x: px - (px - (+z.x || 0)) * r, y: py - (py - (+z.y || 0)) * r }, box);
  }
  // double tap / double click: back to the whole look, or ZOOM_STEP about the tapped point
  function zoomToggle(z, px, py, box) { return z.s > 1 ? { s: 1, x: 0, y: 0 } : zoomAt(z, ZOOM_STEP, px, py, box); }
  function zoomCss(z) { return z.s > 1 ? 'translate(' + z.x.toFixed(1) + 'px, ' + z.y.toFixed(1) + 'px) scale(' + z.s.toFixed(3) + ')' : ''; }

  // honest wait copy (§8: the render takes about 20 s; no percentage, no progress bar): 'wait' ("unos 20 s") until 20 s, 'almost'
  // ("casi listo…") until 35 s, then 'slow' ("Está tardando más de lo normal, seguimos en ello"). The copy only changes at these two points.
  var WAIT_ALMOST_MS = 20000;
  var WAIT_SLOW_MS = 35000;
  function waitStage(elapsedMs) {
    var t = Math.max(0, Number(elapsedMs) || 0);
    return t >= WAIT_SLOW_MS ? 'slow' : t >= WAIT_ALMOST_MS ? 'almost' : 'wait';
  }
  function waitText(elapsedMs, copy) { var c = copy || {}; return c[waitStage(elapsedMs)] || c.wait || ''; }

  // N10 (Tanda 2): the wait clock starts when the render is actually requested (the gateway accepted it, after the photo check), not
  // when CREAR was pressed, so "unos 20 s" / "casi listo…" (20 s) / "tardando" (35 s) follow the render of 13-20 s (§8 of the UX Leader).
  // rec.renderAt: a number = requested at; null = not requested yet (the clock stays at 0, "unos 20 s"); absent (a record saved before
  // this field) = createdAt, as before. Pure, for tests.
  function waitElapsed(rec, now) {
    if (!rec) return 0;
    var t = Number(now) || 0;
    var start = typeof rec.renderAt === 'number' ? rec.renderAt : rec.renderAt === null ? t : (rec.createdAt || t);
    return Math.max(0, t - start);
  }

  // the creating stage (§4.4 "Espera"): the garments of the look appear one by one on a neutral silhouette, each at its body slot. It is an
  // impression of the look, never a render. The slot comes from the record (item.slot, NcTryonConfig bodySlots); an old record without it is
  // matched by NcTryonConfig.slotOf on its title words. Body order top-down (the person is dressed from the head down), at most 5 garments
  // (PV-6); a second garment of a slot and the 'other' ones go to the sides of the figure.
  var WAIT_MAX = 5;
  var WAIT_ORDER = ['head', 'eyes', 'top', 'outer', 'belt', 'bottom', 'socks', 'shoes', 'bag'];
  var WAIT_SIDES = ['side-l', 'side-r', 'side-l2', 'side-r2', 'side-l3'];
  function itemSlot(item, cfg) {
    if (!item) return 'other';
    if (item.slot) return String(item.slot);
    if (!cfg || typeof cfg.slotOf !== 'function') return 'other';
    var words = String(item.title || '').split(/\s+/).filter(Boolean);
    return cfg.slotOf({ collections: item.collections, productType: item.productType, tags: (Array.isArray(item.tags) ? item.tags : []).concat(words) }) || 'other';
  }
  function waitPlan(items, cfg) {
    var list = (items || []).slice(0, WAIT_MAX).map(function (it, i) { var s = itemSlot(it, cfg); var at = WAIT_ORDER.indexOf(s); return { item: it, slot: s, rank: at < 0 ? WAIT_ORDER.length : at, i: i }; });
    list.sort(function (a, b) { return (a.rank - b.rank) || (a.i - b.i); });
    var used = {};
    var side = 0;
    return list.map(function (x, order) {
      var pos = x.rank < WAIT_ORDER.length && !used[x.slot] ? x.slot : WAIT_SIDES[side++];
      used[x.slot] = true;
      return { item: x.item, slot: x.slot, pos: pos, order: order };
    });
  }
  // every copy of the stack of one look (stage, card, thumbnail) runs the same 4 s loop in phase, even when it is painted again
  var WAIT_LOOP_MS = 4000;
  function waitPhase(elapsedMs) { var t = Math.max(0, Math.floor(Number(elapsedMs) || 0)); return -(t % WAIT_LOOP_MS); }

  function fillText(pattern, vars) { return String(pattern || '').replace(/\{(\w+)\}/g, function (all, k) { return vars[k] === undefined ? all : String(vars[k]); }); }
  function pluralText(pattern, n) { var parts = String(pattern || '').split('/'); return parts.length > 1 && n !== 1 ? parts[1] : parts[0]; }

  // "3 prendas · 137,97 €"
  function summary(items, copy) {
    var n = (items || []).length;
    var t = lookTotal(items);
    var count = fillText(pluralText(copy.count || '{n} prenda/{n} prendas', n), { n: n });
    return t === null || !Core ? count : count + ' · ' + Core.formatPrice(t);
  }

  // "Total del look 137,97 € · 3 tiendas"
  function totalLine(items, copy) {
    var t = lookTotal(items);
    var n = storeCount(items);
    var stores = fillText(pluralText(copy.stores || '{n} tienda/{n} tiendas', n), { n: n });
    if (t === null || !Core) return stores;
    return fillText(copy.total || 'Total del look {total} · {stores}', { total: Core.formatPrice(t), stores: stores });
  }

  // a new id for a generated look (the gateway keeps 'gl-...' ids)
  function newId(now, n) { return 'gl-' + Math.max(0, Math.floor(now || 0)).toString(36) + '-' + (n || 0).toString(36); }

  // C2: the two looks a comparison opens with: the selected look (when it is ready, else the newest) on the left and the next ready one on the
  // right (else the previous one). null when fewer than two looks are ready.
  function comparePair(list, selected) {
    var ready = chrono(list).filter(function (r) { return r.status === 'ready'; });
    if (ready.length < 2) return null;
    var at = -1;
    ready.forEach(function (r, i) { if (r.id === selected) at = i; });
    if (at < 0) at = ready.length - 1;
    var other = at + 1 < ready.length ? at + 1 : at - 1;
    return { left: ready[at].id, right: ready[other].id };
  }

  // J5: can this device share an image file? (Web Share level 2; mobile browsers yes, most desktop no, then the button stays hidden)
  function canShareImage() {
    try { return typeof navigator !== 'undefined' && typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && typeof File === 'function' && navigator.canShare({ files: [new File([''], 'x', { type: 'image/png' })] }); } catch (e) { return false; }
  }
  // what the share sheet gets: the image, the AI label as text and the viewer title; never a link
  function shareData(file, label, title) { return { files: [file], text: label, title: title }; }
  // B2: the state of the browser-notification opt-in. 'unsupported' (no Notification API), 'denied' (the browser blocks this site), 'on' (granted AND the
  // person turned it on), 'ask' otherwise. A granted permission alone is not a yes: the person's own tap on the bell is.
  function notifyDecision(supported, permission, optIn) {
    if (!supported) return 'unsupported';
    if (permission === 'denied') return 'denied';
    return permission === 'granted' && optIn ? 'on' : 'ask';
  }
  // the notification is for a person who is away from the tab; with the tab in view the chip and the toast already say it
  function shouldNotify(on, hidden) { return !!on && !!hidden; }

  var api = { pad2: pad2, chrono: chrono, lookNumber: lookNumber, safeUrl: safeUrl, shopLink: shopLink, storeCount: storeCount, lookTotal: lookTotal, sameSet: sameSet, stepIndex: stepIndex, swipeStep: swipeStep, waitStage: waitStage, waitText: waitText, waitElapsed: waitElapsed, waitPlan: waitPlan, waitPhase: waitPhase, itemSlot: itemSlot, WAIT_ALMOST_MS: WAIT_ALMOST_MS, WAIT_SLOW_MS: WAIT_SLOW_MS, WAIT_LOOP_MS: WAIT_LOOP_MS, summary: summary, totalLine: totalLine, newId: newId, notifyDecision: notifyDecision, shouldNotify: shouldNotify, shareData: shareData, comparePair: comparePair, zoomClamp: zoomClamp, zoomAt: zoomAt, zoomToggle: zoomToggle, zoomCss: zoomCss, ZOOM_MAX: ZOOM_MAX, ZOOM_STEP: ZOOM_STEP };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  window.NcLooksCore = api;
  if (typeof customElements === 'undefined' || customElements.get('nc-looks')) return;

  function noop() {}
  // the viewer is inline in the host's stage only on the two-column desktop of the fitting room (§4.2, >= 1000 px of the component, which is a
  // 1064 px window with the theme gutters, 1081 px with a classic scrollbar: N2 of the re-validation); below, the stage sits beside
  // "Mi look actual" (or above it), so the look opens in the sheet instead and its garments are never listed twice
  var INLINE = 1000;
  var SVG_EXT = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';
  // the neutral figure of the creating stage (same proportions as the example figure of the empty stage, 2:3); tokens through currentColor
  var SVG_FIGURE = '<svg class="nc-looks-wait__figure" viewBox="0 0 120 180" aria-hidden="true" focusable="false"><circle cx="60" cy="20" r="11" fill="currentColor"/><path fill="currentColor" d="M60 35c-13 0-22 3-25 8l-8 49c-1 4 5 6 6 2l8-37 1 41 2 70c0 5 9 5 9 0l4-64h6l4 64c0 5 9 5 9 0l2-70 1-41 8 37c1 4 7 2 6-2l-8-49c-3-5-12-8-25-8Z"/></svg>';
  var DOTS = '<span class="nc-looks-dots" aria-hidden="true"><i></i><i></i><i></i></span>';
  var SVG_ALERT = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';
  // Spanish defaults of every list (snippets/nc-looks.liquid writes the section's copy over them)
  var DEFAULTS = {
    // C2: Comparar | dialog title | left select label | right select label | close | note under the pair | look not ready
    compare: ['Comparar', 'Comparar looks', 'Look de la izquierda', 'Look de la derecha', 'Cerrar', 'Elige dos looks para verlos lado a lado. Son imágenes generadas con IA y orientativas.'],
    looks: ['Mis looks', 'Cada look que crees se queda aquí para volver a verlo y compararlo.', 'Look {n}', 'Creando tu look · unos 20 s', 'Está tardando más de lo normal, seguimos en ello', 'No hemos podido crear este look.', 'Reintentar', 'Listo', 'Ver {name}', '{n} prenda/{n} prendas', 'Modo demostración', 'Puedes seguir mirando: te avisamos aquí.', 'Creando tu look · casi listo…', 'Creando tu look', 'unos 20 s', 'casi listo…', 'Crea tu primer look'],
    viewer: ['Tu look', 'Editar', 'Guardar', 'Guardado', 'Compartir', 'Imagen generada con IA', 'Orientativa: el ajuste y el color pueden variar.', 'Prendas del look', 'Total del look {total} · {stores}', '{n} tienda/{n} tiendas', 'Ir a la tienda', 'Ver en Normcore', '{label} de {brand}: {title} (se abre en una pestaña nueva)', 'Look anterior', 'Look siguiente', 'Mis looks', 'La imagen generada llegará cuando el probador esté conectado.', 'Esta imagen ya no está disponible.', 'No se guardó', '{n} de {total}', 'Ampliar', 'Reducir', 'Tamaño original', 'Imagen al {n} %', 'Frente', 'Espalda', 'Generando la espalda…', 'No se pudo generar la espalda. Inténtalo de nuevo.', 'Espalda lista', 'Ver el look de frente o de espaldas'],
    chip: ['Creando tu look…', 'Tu look está listo · Ver', 'No se pudo crear tu look · Ver', 'Cerrar aviso', 'Tu look está listo', 'Creando tu look · unos 20 s. Puedes seguir mirando: te avisamos aquí.', 'Avisarme cuando esté listo, aunque cambie de pestaña', 'Aviso activado: te avisaremos si cambias de pestaña', 'Tu navegador no permite avisos de este sitio', 'Tu look está listo. Vuelve a Normcore para verlo.', 'No se pudo compartir tu look', 'Avisarme también por email', 'Aviso por email activado: te escribimos cuando esté listo', 'No se pudo activar el aviso por email'],
    errors: ['Se interrumpió al salir de la página: vuelve a intentarlo.']
  };

  class NcLooksElement extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      var self = this;
      this.gw = window.NcApp && window.NcApp.gateway ? window.NcApp.gateway() : null;
      this.list = [];
      this.selected = null;
      this.dismissed = {};
      this.chipLook = null;
      this.seq = 0;
      this.m = { compare: this.msgs('data-msg-compare'), looks: this.msgs('data-msg-looks'), viewer: this.msgs('data-msg-viewer'), chip: this.msgs('data-msg-chip'), errors: this.msgs('data-msg-errors') };
      // J5: the viewer shares the generated IMAGE through the device's share sheet (Web Share with files): no public link exists or is invented
      this.shareOn = this.getAttribute('share') !== 'off' && canShareImage();
      this.viewer = this.querySelector('[data-looks-viewer]');
      this.grid = this.querySelector('[data-looks-grid]');
      this.gallery = this.querySelector('[data-looks-gallery]');
      this.sheetWrap = this.querySelector('[data-looks-sheet]');
      this.sheet = this.sheetWrap && this.sheetWrap.querySelector('nc-sheet');
      this.chip = this.querySelector('[data-looks-chip]');
      this.loadNotify();
      this.loadEmailOffer();
      this.loadBackOffer();
      this.live = this.querySelector('[data-looks-live]');
      // the full-screen sheet and the chip leave the host so a hidden host (a closed look sheet) never hides them
      if (this.sheetWrap) document.body.appendChild(this.sheetWrap);
      if (this.chip) document.body.appendChild(this.chip);
      this.addEventListener('click', this.onClick.bind(this));
      if (this.viewer) {
        this.viewer.addEventListener('click', this.onViewerClick.bind(this));
        this.viewer.addEventListener('keydown', this.onViewerKey.bind(this));
        this.viewer.addEventListener('touchstart', function (e) { var t = e.touches && e.touches[0]; if (t) self.touch = { x: t.clientX, y: t.clientY }; }, { passive: true });
        this.viewer.addEventListener('touchend', function (e) {
          var s = self.touch; var t = e.changedTouches && e.changedTouches[0]; self.touch = null;
          // a zoomed look is dragged, not paged
          if (!s || !t || self.zoom.s > 1 || !e.target.closest('[data-lv-media]')) return;
          var d = swipeStep(t.clientX - s.x, t.clientY - s.y);
          if (d) self.step(d);
        }, { passive: true });
      }
      this.zoom = { s: 1, x: 0, y: 0 };
      this.zoomedOnce = {};
      if (this.viewer) this.bindZoom();
      if (this.chip) this.chip.addEventListener('click', this.onChipClick.bind(this));
      if (this.sheetWrap) this.sheetWrap.addEventListener('nc-sheet:close', function (e) { e.stopPropagation(); self.modalOpen = false; self.markModal(false); self.place(); });
      // ↑ / ↓ page the looks from anywhere in the full-screen sheet (its close button has the focus when it opens)
      if (this.sheetWrap) this.sheetWrap.addEventListener('keydown', function (e) { if (self.modalOpen && !self.viewer.contains(e.target)) self.onViewerKey(e); });
      if (typeof ResizeObserver === 'function') new ResizeObserver(function () { self.place(); }).observe(this);
      else window.addEventListener('resize', function () { self.place(); });
      this.load();
    }

    disconnectedCallback() {
      // the portaled pieces go with the element
      if (this.sheetWrap && this.sheetWrap.parentNode === document.body) this.sheetWrap.remove();
      if (this.chip && this.chip.parentNode === document.body) this.chip.remove();
      clearInterval(this.ticker);
      if (this.stageObserver) this.stageObserver.disconnect();
    }

    msgs(attr) { return (this.getAttribute(attr) || '').split('|').map(function (x) { return x.trim(); }); }

    // C2 (J3): two ready looks side by side in a native dialog (Escape closes, focus goes back to the Comparar button). Each side has its own
    // select, so any two looks can be compared; the other side's look is disabled in the list. Images are the looks' own assets, nothing is shown twice.
    openCompare(trigger) {
      var dlg = this.querySelector('[data-looks-compare]');
      var pair = comparePair(this.list, this.selected);
      if (!dlg || !pair || typeof dlg.showModal !== 'function') return;
      var self = this;
      this.cmp = pair;
      if (!dlg.hasAttribute('data-ready')) {
        dlg.setAttribute('data-ready', '');
        dlg.addEventListener('change', function (e) {
          var sel = e.target.closest('[data-cmp-select]');
          if (!sel) return;
          self.cmp[sel.getAttribute('data-cmp-select')] = sel.value;
          self.paintCompare();
        });
        var close = dlg.querySelector('[data-cmp-close]');
        if (close) close.addEventListener('click', function () { dlg.close(); });
        dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
      }
      this.paintCompare();
      this.cmpTrigger = trigger || null;
      dlg.showModal();
      var first = dlg.querySelector('[data-cmp-close]');
      if (first) first.focus();
    }

    paintCompare() {
      var self = this;
      var dlg = this.querySelector('[data-looks-compare]');
      if (!dlg || !this.cmp) return;
      var ready = chrono(this.list).filter(function (r) { return r.status === 'ready'; });
      ['left', 'right'].forEach(function (side) {
        var other = side === 'left' ? self.cmp.right : self.cmp.left;
        var sel = dlg.querySelector('[data-cmp-select="' + side + '"]');
        var rec = self.find(self.cmp[side]);
        sel.textContent = '';
        ready.forEach(function (r) {
          var o = document.createElement('option');
          o.value = r.id;
          o.textContent = fillText(self.tx('looks', 2), { n: lookNumber(self.list, r.id) });
          o.disabled = r.id === other;
          o.selected = r.id === self.cmp[side];
          sel.appendChild(o);
        });
        var slot = dlg.querySelector('[data-cmp-img="' + side + '"]');
        slot.textContent = '';
        slot.setAttribute('data-look', rec ? rec.id : '');
        if (rec) self.applyAsset(slot, rec.assetId, fillText(self.tx('looks', 2), { n: lookNumber(self.list, rec.id) }), true);
        var sum = dlg.querySelector('[data-cmp-summary="' + side + '"]');
        if (sum) sum.textContent = rec ? totalLine(rec.items, { total: self.tx('viewer', 8), stores: self.tx('viewer', 9) }) : '';
        var strip = dlg.querySelector('[data-cmp-items="' + side + '"]');
        if (strip) {
          strip.textContent = '';
          (rec ? rec.items : []).forEach(function (i) { var li = document.createElement('li'); li.textContent = i.title || ''; strip.appendChild(li); });
        }
      });
    }

    // J5 / B7: the generated image goes to the device's share sheet with the AI label in the text. Only a ready, AI-generated look; a cancel says nothing.
    shareLook(id) {
      var self = this;
      var rec = this.find(id);
      if (!rec || rec.status !== 'ready' || !rec.assetId || !this.gw || !this.gw.getAsset || !canShareImage()) return;
      var fail = function () { if (window.NcToast) window.NcToast.show({ tone: 'error', message: self.tx('chip', 10) }); };
      this.gw.getAsset(rec.assetId).then(function (a) {
        if (!a || !a.src) throw new Error('no image');
        return fetch(a.src, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('image ' + r.status); return r.blob(); });
      }).then(function (blob) {
        var file = new File([blob], 'normcore-look.' + (/png/.test(blob.type) ? 'png' : 'jpg'), { type: blob.type || 'image/png' });
        var data = shareData(file, self.tx('viewer', 5), self.tx('viewer', 0));
        if (!navigator.canShare(data)) throw new Error('cannot share');
        return navigator.share(data);
      }).then(null, function (e) { if (!e || e.name !== 'AbortError') fail(); });
    }

    // B2 (email): offered only when the backend says its email provider is configured (getTryOnConfig().emailNotify); the person asks per look
    loadEmailOffer() {
      var self = this;
      this.emailOffered = false;
      this.emailOn = {};
      if (!this.gw || typeof this.gw.getTryOnConfig !== 'function' || typeof this.gw.notifyByEmail !== 'function') return;
      this.gw.getTryOnConfig().then(function (c) { self.emailOffered = !!(c && c.emailNotify); self.renderChip(); }, function () { /* no config: not offered */ });
    }
    // vista de espalda (31-10-2026): a second image of the same generated look, asked for with the Frente / Espalda switch and made on demand.
    // Offered only when the backend switches it on (getTryOnConfig().features.backView) and only on a real ready look (renderId, not demo).
    loadBackOffer() {
      var self = this;
      this.backOffered = false;
      this.backs = {};
      this.backView = {};
      if (!this.gw || typeof this.gw.getTryOnConfig !== 'function' || typeof this.gw.requestLookBack !== 'function') return;
      this.gw.getTryOnConfig().then(function (c) { self.backOffered = !!(c && c.features && c.features.backView); if (self.viewer) self.renderViewer(); }, function () { /* no config: not offered */ });
    }
    backFor(rec) { return !!(this.backOffered && rec && rec.status === 'ready' && rec.renderId && !rec.demo && rec.aiGenerated !== false && rec.assetId); }
    showBack(want) {
      var rec = this.find(this.selected);
      if (!this.backFor(rec)) return;
      this.backView[rec.id] = !!want;
      var b = this.backs[rec.renderId];
      if (want && (!b || b.status === 'failed' || b.status === 'blocked' || b.status === 'none')) this.requestBack(rec);
      this.renderViewer();
    }
    requestBack(rec) {
      var self = this, rid = rec.renderId, n = 0;
      this.backs[rid] = { status: 'queued' };
      this.emit('back', { id: rec.id });
      var tick = function () { self.gw.getLookBack(rid).then(done, function () { done(null); }); };
      var done = function (b) {
        if (!b) b = { status: 'failed' };
        self.backs[rid] = b;
        if (b.status === 'queued' || b.status === 'processing') { if (++n < 60) setTimeout(tick, 2000); else { self.backs[rid] = { status: 'failed' }; self.backDone(rec); } return; }
        self.backDone(rec);
      };
      this.gw.requestLookBack(rid).then(done, function () { done(null); });
    }
    backDone(rec) {
      var b = this.backs[rec.renderId] || {};
      if (b.status === 'ready') { if (this.backView[rec.id]) this.announce(this.tx('viewer', 28)); }
      else { this.backView[rec.id] = false; this.announce(this.tx('viewer', 27)); }
      if (this.selected === rec.id && this.viewer) this.renderViewer();
    }

    toggleEmail() {
      var self = this;
      var id = this.chip.getAttribute('data-look');
      var rec = this.find(id);
      if (!rec || rec.status !== 'generating' || !rec.renderId) return;
      var want = !this.emailOn[id];
      this.gw.notifyByEmail(rec.renderId, want).then(function () {
        self.emailOn[id] = want;
        self.announce(want ? self.tx('chip', 12) : '');
        self.renderChip();
      }, function () { if (window.NcToast) window.NcToast.show({ tone: 'error', message: self.tx('chip', 13) }); });
    }

    // B2 (honest minimum): a browser notification when the look is ready and the person is in another tab or app. It needs the person's own opt-in
    // (the bell of the chip, the browser asks for permission there) and only reaches a page that is still open: no email or push provider exists yet.
    notifySupported() { return typeof Notification !== 'undefined'; }
    notifyOn() { return notifyDecision(this.notifySupported(), this.notifySupported() ? Notification.permission : 'denied', this.notifyOptIn) === 'on'; }
    toggleNotify() {
      var self = this;
      if (!this.notifySupported()) return;
      if (this.notifyOn()) { this.notifyOptIn = false; this.storeNotify(false); this.renderChip(); return; }
      if (Notification.permission === 'denied') { this.announce(this.tx('chip', 8)); return; }
      var done = function (perm) {
        self.notifyOptIn = perm === 'granted';
        self.storeNotify(self.notifyOptIn);
        self.announce(self.notifyOptIn ? self.tx('chip', 7) : self.tx('chip', 8));
        self.renderChip();
      };
      var asked = Notification.requestPermission(done);
      if (asked && typeof asked.then === 'function') asked.then(done);
    }
    storeNotify(on) { try { if (on) window.localStorage.setItem('nc-looks-notify', '1'); else window.localStorage.removeItem('nc-looks-notify'); } catch (e) { /* storage blocked: the choice lasts this page only */ } }
    loadNotify() { try { this.notifyOptIn = window.localStorage.getItem('nc-looks-notify') === '1'; } catch (e) { this.notifyOptIn = false; } }
    // the ready look: a notification only when the person is away from this tab (visible tab already shows the chip and the toast)
    notifyReady(id) {
      var self = this;
      if (!this.notifyOn() || !shouldNotify(true, document.hidden)) return;
      try {
        var n = new Notification(this.tx('chip', 4), { body: this.tx('chip', 9), tag: 'nc-look-' + id });
        n.onclick = function () { window.focus(); self.open(id); n.close(); };
      } catch (e) { /* some browsers only allow notifications from a service worker: the tab title below still tells */ }
      var title = document.title;
      document.title = '\u2713 ' + this.tx('chip', 4) + ' \u00b7 ' + title;
      var back = function () { if (document.hidden) return; document.title = title; document.removeEventListener('visibilitychange', back); };
      document.addEventListener('visibilitychange', back);
    }

    tx(group, i) { return (this.m[group] && this.m[group][i]) || DEFAULTS[group][i] || ''; }

    announce(text) { if (this.live) { this.live.textContent = ''; var el = this.live; setTimeout(function () { el.textContent = text; }, 30); } }

    emit(name, detail) { return this.dispatchEvent(new CustomEvent('nc-looks:' + name, { bubbles: true, detail: detail || {} })); }

    // ---- data ----
    load() {
      var self = this;
      if (!this.gw || typeof this.gw.listGeneratedLooks !== 'function') { this.render(); return; }
      this.gw.listGeneratedLooks().then(function (res) {
        var mine = self.list.slice();
        self.list = (res.items || []).slice();
        mine.forEach(function (r) { if (!self.find(r.id)) self.list.unshift(r); });
        self.list.forEach(function (r) { if (r.status === 'generating' && !self.isLive(r.id)) self.follow(r); });
        self.render();
      }, function () { self.render(); });
    }

    find(id) { return this.list.filter(function (r) { return r.id === id; })[0] || null; }

    // looks being created by this page right now (the host marks them); the others are followed through the gateway or marked interrupted
    isLive(id) { return !!(this.liveIds && this.liveIds[id]); }

    persist(rec) {
      if (this.gw && typeof this.gw.saveGeneratedLook === 'function') this.gw.saveGeneratedLook(rec).then(noop, noop);
    }

    add(rec) {
      this.liveIds = this.liveIds || {};
      this.liveIds[rec.id] = true;
      var at = this.list.map(function (r) { return r.id; }).indexOf(rec.id);
      if (at >= 0) this.list.splice(at, 1);
      this.list.unshift(rec);
      this.persist(rec);
      // the newest look is the selected one (inline stage, stage preview); an open viewer sheet keeps the look it shows
      if (!this.modalOpen) this.selected = rec.id;
      this.dismissed = {};
      this.chipLook = rec.id;
      this.render();
      this.announce(this.tx('chip', 5));
      return rec;
    }

    update(id, patch) {
      var rec = this.find(id);
      if (!rec) return null;
      Object.assign(rec, patch || {});
      if (rec.status !== 'generating' && this.liveIds) delete this.liveIds[id];
      this.persist(rec);
      var finished = !!(patch && patch.status && patch.status !== 'generating');
      if (finished) {
        this.chipLook = id;
        delete this.dismissed[id];
        this.announce(patch.status === 'ready' ? this.tx('chip', 4) : this.errorText(rec));
        if (patch.status === 'ready') this.notifyReady(id);
      }
      this.render();
      if (finished) this.revealResult(id);
      return rec;
    }

    remove(id) {
      this.list = this.list.filter(function (r) { return r.id !== id; });
      if (this.liveIds) delete this.liveIds[id];
      if (this.gw && typeof this.gw.removeGeneratedLook === 'function') this.gw.removeGeneratedLook(id).then(noop, noop);
      if (this.selected === id) this.selected = null;
      if (this.chipLook === id) this.chipLook = null;
      this.render();
    }

    // a look that was being created when the page was left: follow its render if the gateway still knows it, else say it was interrupted
    follow(rec) {
      var self = this;
      var stop = function () { self.update(rec.id, { status: 'error', message: self.tx('errors', 0) }); };
      if (!rec.renderId || !this.gw || typeof this.gw.getLookRender !== 'function') { stop(); return; }
      var n = 0;
      var tick = function () {
        self.gw.getLookRender(rec.renderId).then(function (r) {
          if (!self.find(rec.id)) return;
          if (r.status === 'ready') { self.update(rec.id, { status: 'ready', assetId: r.assetId || null, aiGenerated: r.aiGenerated !== false }); return; }
          if (r.status === 'queued' || r.status === 'processing') { if (++n < 60) setTimeout(tick, 1000); else stop(); return; }
          stop();
        }, stop);
      };
      tick();
    }

    generating() { return this.list.filter(function (r) { return r.status === 'generating'; }); }

    // ---- placement: inline in the host's stage when wide, the full-screen sheet otherwise ----
    setInlineHost(el) { this.inlineHost = el || null; this.watchStage(el); this.place(); }

    // the host's stage preview below the two-column desktop: [data-looks-preview-media] (a button: the look's picture, the entry to the
    // viewer sheet), [data-looks-preview-title] and [data-looks-preview-line] (written only while the look is being created)
    setPreviewHost(el) {
      var self = this;
      this.previewHost = el || null;
      var media = el && el.querySelector('[data-looks-preview-media]');
      if (media && !media.hasAttribute('data-looks-bound')) {
        media.setAttribute('data-looks-bound', '');
        media.addEventListener('click', function () { var id = media.getAttribute('data-id'); if (id) self.open(id, media); });
      }
      this.watchStage(el);
      this.paintPreview();
    }

    // is the stage that shows the look on screen? A ready look seen there needs no "Tu look está listo · Ver" chip
    watchStage(el) {
      var self = this;
      var stage = el && (el.closest('.nc-fr__stage') || el);
      if (!stage || stage === this.watched || typeof IntersectionObserver !== 'function') return;
      this.watched = stage;
      if (this.stageObserver) this.stageObserver.disconnect();
      this.stageObserver = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        var seen = !!(e && e.isIntersecting && e.intersectionRect.height >= Math.min(160, e.boundingClientRect.height / 2));
        if (seen === self.stageSeen) return;
        self.stageSeen = seen;
        self.renderChip();
      }, { threshold: [0, 0.25, 0.5, 1] });
      this.stageObserver.observe(stage);
    }

    // the look the stage shows: the inline viewer's selected look, or (preview) the newest look being created, else the selected one
    stageLook() {
      if (this.isInline()) return this.find(this.selected);
      return this.generating()[0] || this.find(this.selected) || this.list[0] || null;
    }

    paintPreview() {
      var host = this.previewHost;
      if (!host) return;
      var self = this;
      var rec = this.isInline() ? null : this.stageLook();
      var state = !rec ? 'empty' : rec.status === 'generating' ? 'generating' : 'look';
      if (host.getAttribute('data-looks-preview') !== state) host.setAttribute('data-looks-preview', state);
      var media = host.querySelector('[data-looks-preview-media]');
      if (!media) return;
      media.hidden = !rec;
      if (!rec) { media.removeAttribute('data-id'); media.removeAttribute('data-sig'); media.textContent = ''; return; }
      var name = fillText(this.tx('looks', 2), { n: lookNumber(this.list, rec.id) });
      var label = fillText(this.tx('looks', 8), { name: name }) + '. ' + this.statusText(rec);
      if (media.getAttribute('aria-label') !== label) media.setAttribute('aria-label', label);
      var sig = rec.id + '|' + rec.status + '|' + (rec.assetId || '') + '|' + (rec.demo ? 1 : 0);
      var box = media.querySelector(':scope > .nc-looks-preview');
      if (!box || media.getAttribute('data-sig') !== sig) {
        media.setAttribute('data-sig', sig);
        media.setAttribute('data-id', rec.id);
        media.setAttribute('data-state', rec.status);
        media.textContent = '';
        box = document.createElement('span');
        box.className = 'nc-looks-preview';
        media.appendChild(box);
        if (rec.status === 'ready' && rec.assetId && !rec.demo) this.applyAsset(box, rec.assetId, '', true, function (a) { if (!a) self.mosaic(box, rec); });
        else if (rec.status !== 'generating') this.mosaic(box, rec);
      }
      if (rec.status !== 'generating') return;
      this.waitStack(box, rec, 'card');
      var title = host.querySelector('[data-looks-preview-title]');
      if (title && title.textContent !== this.tx('looks', 13)) title.textContent = this.tx('looks', 13);
      var line = host.querySelector('[data-looks-preview-line]');
      if (!line) return;
      var text = this.waitLine(rec);
      var span = line.querySelector('[data-looks-preview-text]');
      if (!span) {
        line.textContent = '';
        line.insertAdjacentHTML('afterbegin', DOTS);
        span = document.createElement('span');
        span.setAttribute('data-looks-preview-text', '');
        line.appendChild(span);
      }
      if (span.textContent !== text) span.textContent = text;
    }

    // a look has just finished: its stage comes into view when the stage top is off screen (instant with reduced motion). On the two-column
    // desktop that is the inline viewer (N3 of the re-validation); on tablet the stage preview, unless the person is looking at the look's
    // card of "Mis looks" (the chip took them there). The compact mobile row is never scrolled to: the sticky bar's create already did it.
    revealResult(id) {
      if (this.modalOpen || this.selected !== id) return;
      var host = null;
      if (this.isInline()) host = this.inlineHost;
      else {
        var box = this.closest('.nc-fr__box') || this;
        var card = this.cardButton(id);
        var cr = card && card.getBoundingClientRect();
        if (box.getBoundingClientRect().width < 700 || (cr && cr.bottom > 0 && cr.top < window.innerHeight)) return;
        host = this.previewHost;
      }
      if (!host) return;
      var stage = host.closest('.nc-fr__stage') || host;
      var r = stage.getBoundingClientRect();
      if (r.top >= 0 && r.top < window.innerHeight) return;
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      stage.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
    }

    // keyboard path (N7): once a look created from the keyboard is ready, the focus goes to the viewer's first action (inline) or to the
    // stage preview that opens it; the host only calls this when the create was keyboard-triggered and the focus is still where it was
    focusResult(id) {
      if (this.modalOpen) return false;
      var target = null;
      if (this.isInline()) target = this.selected === id && this.viewer ? this.viewer.querySelector('[data-lv-edit]') : null;
      else if (this.previewHost) {
        var media = this.previewHost.querySelector('[data-looks-preview-media]');
        target = media && media.getAttribute('data-id') === id ? media : null;
      }
      if (!target || !target.getClientRects().length) return false;
      target.focus({ preventScroll: this.isInline() });
      return true;
    }

    isInline() {
      if (!this.inlineHost || !this.inlineHost.isConnected) return false;
      var box = this.closest('.nc-fr__box') || this;
      return box.getBoundingClientRect().width >= INLINE;
    }

    place() {
      if (!this.viewer) return;
      var inline = this.isInline();
      if (inline && !this.modalOpen) {
        if (this.viewer.parentNode !== this.inlineHost) this.inlineHost.appendChild(this.viewer);
        this.viewer.setAttribute('data-mode', 'inline');
        this.inlineHost.hidden = !this.list.length;
        this.viewer.hidden = !this.list.length;
        if (this.inlineHost.parentNode) this.inlineHost.parentNode.toggleAttribute('data-fr-has-looks', this.list.length > 0);
      } else if (!this.modalOpen) {
        if (this.inlineHost) { this.inlineHost.hidden = true; if (this.inlineHost.parentNode) this.inlineHost.parentNode.toggleAttribute('data-fr-has-looks', false); }
        if (this.viewer.parentNode !== this) this.appendChild(this.viewer);
        this.viewer.hidden = true;
      }
      if (this.wasInline !== inline) { this.wasInline = inline; this.paintPreview(); }
      this.syncChipLift();
    }

    // ---- open / navigate ----
    open(id, trigger) {
      var rec = this.find(id) || this.list[0];
      if (!rec) return;
      this.selected = rec.id;
      if (this.chipLook === rec.id && rec.status !== 'generating') this.dismissed[rec.id] = true;
      this.emit('open', { id: rec.id });
      if (this.isInline()) {
        this.place();
        this.renderViewer();
        this.renderChip();
        var r = this.inlineHost.getBoundingClientRect();
        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (r.top < 0 || r.top > window.innerHeight * 0.6) this.inlineHost.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
        this.viewer.focus({ preventScroll: true });
        return;
      }
      if (!this.sheet || !this.sheet.open) return;
      var panel = this.sheet.querySelector('.nc-sheet__panel');
      if (this.viewer.parentNode !== panel) panel.appendChild(this.viewer);
      this.viewer.setAttribute('data-mode', 'modal');
      this.viewer.hidden = false;
      this.modalOpen = true;
      this.markModal(true);
      this.renderViewer();
      this.renderChip();
      var opener = trigger && trigger.isConnected && trigger.getClientRects().length ? trigger : this.cardButton(rec.id) || this.fallbackTrigger || null;
      this.sheet.open(opener);
    }

    // while the viewer sheet is open, the host's look panel (the same garments) is not painted behind it (data-looks-modal on the room box)
    markModal(on) {
      var box = this.closest('.nc-fr__box');
      if (box) box.toggleAttribute('data-looks-modal', !!on);
    }

    cardButton(id) {
      var b = this.grid && this.grid.querySelector('[data-looks-open="' + id + '"]');
      return b && b.getClientRects().length ? b : null;
    }

    step(delta) {
      var order = chrono(this.list);
      var ids = order.map(function (r) { return r.id; });
      var at = stepIndex(ids.indexOf(this.selected), delta, ids.length);
      if (at < 0 || ids[at] === this.selected) return;
      this.selected = ids[at];
      this.renderViewer();
      var rec = this.find(this.selected);
      this.announce(fillText(this.tx('looks', 2), { n: lookNumber(this.list, rec.id) }) + ', ' + this.statusText(rec));
    }

    statusText(rec) {
      if (rec.status === 'generating') return waitText(this.elapsed(rec), { wait: this.tx('looks', 3), almost: this.tx('looks', 12), slow: this.tx('looks', 4) });
      if (rec.status === 'error') return this.errorText(rec);
      return rec.demo ? this.tx('looks', 10) : this.tx('looks', 7);
    }

    elapsed(rec) { return waitElapsed(rec, Date.now()); }

    // the line under "Creando tu look" on the stage: "unos 20 s" → "casi listo…" (20 s) → "Está tardando más de lo normal, seguimos en ello" (35 s)
    waitLine(rec) { return waitText(this.elapsed(rec), { wait: this.tx('looks', 14), almost: this.tx('looks', 15), slow: this.tx('looks', 4) }); }

    // the garments of a look being created, appearing one by one on the neutral figure (size: stage | card | thumb). Painted again only when
    // the garments change, and always in phase with the elapsed time, so a repaint never restarts the loop. Reduced motion: static (CSS).
    waitStack(host, rec, size) {
      var self = this;
      var plan = waitPlan(rec.items, typeof window !== 'undefined' ? window.NcTryonConfig : null);
      var sig = rec.id + '|' + plan.map(function (p) { return (p.item.assetId || p.item.productId || '') + ':' + p.pos; }).join(',');
      var stack = host.querySelector(':scope > .nc-looks-wait');
      if (!stack || stack.getAttribute('data-sig') !== sig) {
        host.textContent = '';
        stack = document.createElement('span');
        stack.className = 'nc-looks-wait';
        stack.setAttribute('data-size', size);
        stack.setAttribute('data-count', String(plan.length));
        stack.setAttribute('data-sig', sig);
        stack.setAttribute('aria-hidden', 'true');
        stack.innerHTML = SVG_FIGURE;
        plan.forEach(function (p) {
          var g = document.createElement('span');
          g.className = 'nc-looks-wait__g';
          g.setAttribute('data-pos', p.pos);
          g.setAttribute('data-i', String(p.order));
          stack.appendChild(g);
          self.applyAsset(g, p.item.assetId, '', size === 'stage');
        });
        host.appendChild(stack);
      }
      stack.style.setProperty('--nc-looks-wait-phase', waitPhase(this.elapsed(rec)) + 'ms');
      return stack;
    }

    // "No hemos podido crear este look." + the reason the host gave (e.g. "Esta prueba no cuenta en tu cupo de hoy.")
    errorText(rec) { return this.tx('looks', 5) + (rec.message ? ' ' + rec.message : ''); }

    // ---- rendering ----
    render() {
      if (!this.selected || !this.find(this.selected)) this.selected = this.list.length ? this.list[0].id : null;
      this.renderGallery();
      this.place();
      this.renderViewer();
      this.paintPreview();
      this.renderChip();
      this.syncTicker();
      // a failed attempt is not a look (F4): `count` is the looks that exist, `attempts` also counts the failed ones
      this.emit('change', { count: this.list.filter(function (r) { return r.status !== 'error'; }).length, attempts: this.list.length, generating: this.generating().length });
    }

    applyAsset(slot, assetId, alt, eager, onDone) {
      var self = this;
      if (!slot || !assetId || !this.gw || !this.gw.getAsset) { if (onDone) onDone(null); return; }
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !slot.isConnected) { if (onDone) onDone(null); return; }
        var img = document.createElement('img');
        img.src = a.src;
        img.width = a.width || 841;
        img.height = a.height || 1264;
        img.alt = alt || a.alt || '';
        img.loading = eager ? 'eager' : 'lazy';
        img.decoding = 'async';
        slot.textContent = '';
        slot.appendChild(img);
        if (onDone) onDone(a);
      }, function () { if (onDone) onDone(null); self = null; });
    }

    // the garments of a look side by side: what a card / the stage shows when there is no generated image (honest, never a fake render)
    mosaic(host, rec) {
      var self = this;
      host.textContent = '';
      var ul = document.createElement('ul');
      ul.className = 'nc-looks-mosaic';
      ul.setAttribute('data-count', String(Math.min(4, rec.items.length)));
      ul.setAttribute('aria-hidden', 'true');
      rec.items.slice(0, 4).forEach(function (it) {
        var li = document.createElement('li');
        li.className = 'nc-looks-mosaic__cell';
        ul.appendChild(li);
        self.applyAsset(li, it.assetId, '', false);
      });
      host.appendChild(ul);
    }

    renderGallery() {
      var self = this;
      if (!this.grid) return;
      var count = this.querySelector('[data-looks-count]');
      // a failed render is not a look: it never counts in "Mis looks" (probador unificado, M2/C4)
      var made = this.list.filter(function (r) { return r.status !== 'error'; }).length;
      if (count) count.textContent = made ? '(' + made + ')' : '';
      var empty = this.querySelector('[data-looks-empty]');
      if (empty) empty.hidden = this.list.length > 0;
      this.grid.hidden = !this.list.length;
      var keep = {};
      this.list.forEach(function (rec) {
        keep[rec.id] = true;
        var li = self.grid.querySelector('[data-id="' + rec.id + '"]');
        var sig = rec.status + '|' + (rec.assetId || '') + '|' + (rec.demo ? 1 : 0) + '|' + (rec.message || '');
        if (!li) { li = document.createElement('li'); li.className = 'nc-looks__card'; li.setAttribute('data-id', rec.id); }
        if (li.getAttribute('data-sig') !== sig) { self.paintCard(li, rec); li.setAttribute('data-sig', sig); }
        self.grid.appendChild(li); // newest first: the list order
      });
      Array.prototype.forEach.call(this.grid.querySelectorAll('.nc-looks__card'), function (li) { if (!keep[li.getAttribute('data-id')]) li.remove(); });
      this.paintWaits();
    }

    paintCard(li, rec) {
      var num = lookNumber(this.list, rec.id);
      var name = fillText(this.tx('looks', 2), { n: num });
      // creating → ready / error: the new content cross-fades in (180 ms, 80 ms with reduced motion)
      li.toggleAttribute('data-enter', li.getAttribute('data-state') === 'generating' && rec.status !== 'generating');
      li.setAttribute('data-state', rec.status);
      li.textContent = '';
      var open = document.createElement('button');
      open.type = 'button';
      open.className = 'nc-looks__open';
      open.setAttribute('data-looks-open', rec.id);
      var media = document.createElement('span');
      media.className = 'nc-looks__media';
      var badge = document.createElement('span');
      badge.className = 'nc-looks__num';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = num;
      var status = document.createElement('span');
      status.className = 'nc-looks__status';
      status.setAttribute('data-looks-status', '');
      if (rec.status === 'generating') status.insertAdjacentHTML('afterbegin', DOTS);
      if (rec.status === 'error') status.insertAdjacentHTML('afterbegin', SVG_ALERT);
      var statusText = document.createElement('span');
      statusText.setAttribute('data-looks-status-text', '');
      statusText.textContent = this.statusText(rec);
      status.appendChild(statusText);
      var meta = document.createElement('span');
      meta.className = 'nc-looks__meta';
      var strong = document.createElement('strong');
      strong.textContent = name;
      var sub = document.createElement('span');
      sub.textContent = summary(rec.items, { count: this.tx('looks', 9) });
      meta.appendChild(strong);
      meta.appendChild(sub);
      open.appendChild(media);
      open.appendChild(badge);
      // F10: a generated picture says so in the gallery too (a thumbnail is seen without opening the viewer); same wording as the viewer's note
      var aiShown = rec.status === 'ready' && rec.assetId && !rec.demo && rec.aiGenerated !== false;
      if (aiShown) {
        var ai = document.createElement('span');
        ai.className = 'nc-looks__ai';
        ai.setAttribute('aria-hidden', 'true');
        ai.setAttribute('title', this.tx('viewer', 5));
        ai.textContent = 'IA';
        open.appendChild(ai);
      }
      open.appendChild(status);
      open.appendChild(meta);
      open.setAttribute('aria-label', fillText(this.tx('looks', 8), { name: name }) + '. ' + sub.textContent + '. ' + this.statusText(rec) + (aiShown ? '. ' + this.tx('viewer', 5) : ''));
      li.appendChild(open);
      var self = this;
      if (rec.status === 'ready' && rec.assetId && !rec.demo) {
        this.applyAsset(media, rec.assetId, '', false, function (a) { if (!a) self.mosaic(media, rec); });
      } else if (rec.status === 'generating') this.waitStack(media, rec, 'card');
      else this.mosaic(media, rec);
      if (rec.status === 'error') {
        var retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'nc-looks__retry';
        retry.setAttribute('data-looks-retry', rec.id);
        retry.textContent = this.tx('looks', 6);
        retry.setAttribute('aria-label', this.tx('looks', 6) + ': ' + name);
        li.appendChild(retry);
      }
    }

    // the wait copy changes at 20 s and 35 s: one ticker while a look is being created (it only writes when a text changes)
    syncTicker() {
      var self = this;
      var any = this.generating().length > 0;
      if (any && !this.ticker) this.ticker = setInterval(function () { self.paintWaits(); }, 1000);
      if (!any && this.ticker) { clearInterval(this.ticker); this.ticker = null; }
    }

    paintWaits() {
      var self = this;
      this.waitStages = this.waitStages || {};
      this.generating().forEach(function (rec) {
        var text = self.statusText(rec);
        var card = self.grid && self.grid.querySelector('[data-id="' + rec.id + '"] [data-looks-status-text]');
        if (card && card.textContent !== text) {
          card.textContent = text;
          var open = card.closest('[data-looks-open]');
          if (open) open.setAttribute('aria-label', fillText(self.tx('looks', 8), { name: fillText(self.tx('looks', 2), { n: lookNumber(self.list, rec.id) }) }) + '. ' + summary(rec.items, { count: self.tx('looks', 9) }) + '. ' + text);
        }
        if (self.selected === rec.id && self.viewer) {
          var w = self.viewer.querySelector('[data-lv-wait-text]');
          var line = self.waitLine(rec);
          if (w && w.textContent !== line) w.textContent = line;
        }
        var thumb = self.viewer && self.viewer.querySelector('[data-lv-go="' + rec.id + '"]');
        if (thumb) thumb.setAttribute('aria-label', fillText(self.tx('looks', 2), { n: lookNumber(self.list, rec.id) }) + ', ' + text);
        // one polite announcement at each copy change (20 s, 35 s), never at every tick
        var stage = waitStage(self.elapsed(rec));
        var was = self.waitStages[rec.id];
        self.waitStages[rec.id] = stage;
        if (was && was !== stage) { self.announce(text); self.emit('wait', { id: rec.id, stage: stage, text: text }); }
      });
      this.paintPreview();
      this.renderChip();
    }

    renderViewer() {
      var v = this.viewer;
      if (!v) return;
      var rec = this.find(this.selected);
      if (!rec) return;
      var self = this;
      var num = lookNumber(this.list, rec.id);
      var name = fillText(this.tx('looks', 2), { n: num });
      v.setAttribute('aria-label', this.tx('viewer', 0) + ': ' + name);
      v.setAttribute('data-state', rec.status);
      var heading = v.querySelector('[data-lv-heading]');
      if (heading) heading.textContent = name;
      if (this.sheet) { var st = this.sheet.querySelector('.nc-sheet__title'); if (st) st.textContent = this.tx('viewer', 0) + ' · ' + num; }
      // media
      var media = v.querySelector('[data-lv-media]');
      var img = v.querySelector('[data-lv-img]');
      var ai = v.querySelector('[data-lv-ai]');
      var demo = v.querySelector('[data-lv-demo]');
      var note = v.querySelector('[data-lv-note]');
      var wait = v.querySelector('[data-lv-wait]');
      var err = v.querySelector('[data-lv-error]');
      var gone = v.querySelector('[data-lv-gone]');
      // creating → ready / error on the same look: the creating stage stays on top and cross-fades out (180 ms) once the result is painted
      var handover = !!(this.shown && this.shown.id === rec.id && this.shown.status === 'generating' && rec.status !== 'generating');
      // vista de espalda: the back replaces the front picture once it is ready; until then the front stays and the switch says it is coming
      var canBack = this.backFor(rec), back = canBack ? this.backs[rec.renderId] : null;
      var wantBack = !!(canBack && this.backView[rec.id]);
      var face = wantBack && back && back.status === 'ready' && back.assetId ? 'back' : 'front';
      var faceAsset = face === 'back' ? back.assetId : rec.assetId;
      // another look, this one no longer ready, or the other side of it: the zoom starts again from the whole look
      if (!this.shown || this.shown.id !== rec.id || rec.status !== 'ready' || this.shown.face !== face) { media.setAttribute('data-zoom-ready', 'false'); this.resetZoom(); }
      this.shown = { id: rec.id, status: rec.status, face: face };
      clearTimeout(this.leaveTimer);
      wait.removeAttribute('data-leaving');
      media.setAttribute('data-state', rec.status);
      ai.hidden = true;
      note.hidden = true;
      demo.hidden = !(rec.status === 'ready' && rec.demo);
      wait.hidden = rec.status !== 'generating' && !handover;
      var leave = function () {
        if (!handover || seq !== self.seq) return;
        wait.setAttribute('data-leaving', '');
        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        self.leaveTimer = setTimeout(function () { if (seq === self.seq) { wait.hidden = true; wait.removeAttribute('data-leaving'); } }, still ? 80 : 180);
      };
      err.hidden = rec.status !== 'error';
      gone.hidden = true;
      var demoNote = v.querySelector('[data-lv-demo-note]');
      if (demoNote) demoNote.hidden = !(rec.status === 'ready' && rec.demo);
      var seq = ++this.seq;
      if (rec.status === 'ready' && rec.assetId && !rec.demo) {
        img.textContent = '';
        var alt = name + (face === 'back' ? ' (' + this.tx('viewer', 25) + ')' : '') + ': ' + rec.items.map(function (i) { return i.title; }).join(', ');
        media.setAttribute('data-face', face);
        this.applyAsset(img, faceAsset, alt, true, function (a) {
          if (seq !== self.seq) return;
          leave();
          if (!a) { self.mosaic(img, rec); gone.hidden = false; return; }
          var image = img.querySelector('img');
          if (image && rec.aiGenerated !== false) image.setAttribute('data-ai-generated', 'true');
          if (image) { media.setAttribute('data-zoom-ready', 'true'); self.setZoom(self.zoom); }
          // AI Act art. 50 (D-IA): the label is fixed over a generated image and the note goes under it; neither can be closed
          ai.hidden = rec.aiGenerated === false;
          note.hidden = rec.aiGenerated === false;
        });
      } else if (rec.status === 'generating') img.textContent = '';
      else { this.mosaic(img, rec); leave(); }
      if (rec.status === 'generating') {
        var title = v.querySelector('[data-lv-wait-title]');
        if (title) title.textContent = this.tx('looks', 13);
        v.querySelector('[data-lv-wait-text]').textContent = this.waitLine(rec);
        var fig = v.querySelector('[data-lv-wait-figure]');
        if (fig) this.waitStack(fig, rec, 'stage');
      }
      if (rec.status === 'error') v.querySelector('[data-lv-error-text]').textContent = this.errorText(rec);
      // Frente / Espalda switch (vista de espalda)
      var vb = v.querySelector('[data-lv-view-bar]');
      if (vb) {
        vb.hidden = !canBack;
        var busy = wantBack && face !== 'back';
        vb.setAttribute('aria-label', this.tx('viewer', 29));
        Array.prototype.forEach.call(vb.querySelectorAll('[data-lv-view]'), function (btn) {
          var isBack = btn.getAttribute('data-lv-view') === 'back';
          btn.setAttribute('aria-pressed', String(isBack === wantBack));
          var lab = btn.querySelector('[data-lv-view-label]');
          if (lab) lab.textContent = self.tx('viewer', isBack ? 25 : 24);
          if (isBack) btn.setAttribute('aria-busy', busy ? 'true' : 'false');
        });
        var vs = vb.querySelector('[data-lv-view-status]');
        if (vs) { vs.hidden = !busy; vs.textContent = busy ? this.tx('viewer', 26) : ''; }
      }
      // actions
      var save = v.querySelector('[data-lv-save]');
      if (save) {
        save.setAttribute('aria-pressed', rec.saved ? 'true' : 'false');
        save.disabled = rec.status !== 'ready';
        var sl = save.querySelector('[data-lv-save-label]');
        if (sl) sl.textContent = rec.saved ? this.tx('viewer', 3) : this.tx('viewer', 2);
        save.setAttribute('aria-label', (rec.saved ? this.tx('viewer', 3) : this.tx('viewer', 2)) + ': ' + name);
      }
      var share = v.querySelector('[data-lv-share]');
      if (share) { share.hidden = !this.shareOn; share.disabled = rec.status !== 'ready'; }
      var cmp = v.querySelector('[data-lv-compare]');
      if (cmp) cmp.hidden = !comparePair(this.list, this.selected);
      // pager
      this.renderPager(rec);
      // garments
      var strip = v.querySelector('[data-lv-strip]');
      strip.textContent = '';
      rec.items.forEach(function (it) { strip.appendChild(self.garmentRow(it)); });
      v.querySelector('[data-lv-total]').textContent = totalLine(rec.items, { total: this.tx('viewer', 8), stores: this.tx('viewer', 9) });
    }

    renderPager(rec) {
      var v = this.viewer;
      var self = this;
      var order = chrono(this.list);
      var at = order.map(function (r) { return r.id; }).indexOf(rec.id);
      var pager = v.querySelector('[data-lv-pager]');
      // inline (desktop) the thumbnails are the gallery: the column shows from the first look; in the sheet the pager needs two
      pager.hidden = order.length < (v.getAttribute('data-mode') === 'inline' ? 1 : 2);
      var thumbs = v.querySelector('[data-lv-thumbs]');
      thumbs.textContent = '';
      order.forEach(function (r, i) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nc-lv__thumb';
        b.setAttribute('data-lv-go', r.id);
        b.setAttribute('data-state', r.status);
        var n = pad2(i + 1);
        b.setAttribute('aria-label', fillText(self.tx('looks', 2), { n: n }) + ', ' + self.statusText(r));
        if (r.id === rec.id) b.setAttribute('aria-current', 'true');
        var m = document.createElement('span');
        m.className = 'nc-lv__thumb-media';
        var label = document.createElement('span');
        label.className = 'nc-lv__thumb-num';
        label.setAttribute('aria-hidden', 'true');
        label.textContent = n;
        b.appendChild(m);
        b.appendChild(label);
        li.appendChild(b);
        thumbs.appendChild(li);
        // a look still being created shows the same garment stack as the stage, in miniature (no plain spinner)
        if (r.status === 'generating') self.waitStack(m, r, 'thumb');
        else if (r.status === 'ready' && r.assetId && !r.demo) self.applyAsset(m, r.assetId, '', false);
        else if (r.items[0]) self.applyAsset(m, r.items[0].assetId, '', false);
      });
      var counter = v.querySelector('[data-lv-counter]');
      if (counter) counter.textContent = pad2(at + 1) + ' / ' + pad2(order.length);
      Array.prototype.forEach.call(v.querySelectorAll('[data-lv-step]'), function (b) {
        var d = parseInt(b.getAttribute('data-lv-step'), 10);
        var off = d < 0 ? at <= 0 : at >= order.length - 1;
        b.setAttribute('aria-disabled', off ? 'true' : 'false');
      });
    }

    garmentRow(it) {
      var li = document.createElement('li');
      li.className = 'nc-lv__item';
      var media = document.createElement('span');
      media.className = 'nc-lv__item-media';
      li.appendChild(media);
      this.applyAsset(media, it.assetId, '', false);
      var copy = document.createElement('div');
      copy.className = 'nc-lv__item-copy';
      if (it.brand) {
        var brand = document.createElement('p');
        brand.className = 'nc-lv__item-brand';
        brand.textContent = it.brand;
        copy.appendChild(brand);
      }
      var page = safeUrl(it.url);
      var title = document.createElement(page ? 'a' : 'p');
      title.className = 'nc-lv__item-title';
      title.textContent = it.title;
      if (page) title.href = page;
      copy.appendChild(title);
      if (it.price) {
        var price = document.createElement('p');
        price.className = 'nc-lv__item-price';
        price.textContent = it.price;
        copy.appendChild(price);
      }
      li.appendChild(copy);
      var link = shopLink(it);
      if (link.href) {
        var a = document.createElement('a');
        a.className = 'nc-lv__shop';
        a.href = link.href;
        a.target = '_blank';
        a.rel = 'noopener';
        a.setAttribute('data-kind', link.kind);
        var label = link.kind === 'brand' ? this.tx('viewer', 10) : this.tx('viewer', 11);
        a.setAttribute('aria-label', fillText(this.tx('viewer', 12), { label: label, brand: it.brand || 'Normcore', title: it.title }));
        var span = document.createElement('span');
        span.textContent = label;
        a.appendChild(span);
        a.insertAdjacentHTML('beforeend', SVG_EXT);
        li.appendChild(a);
      }
      return li;
    }

    // ---- chip ----
    renderChip() {
      if (!this.chip) return;
      var box = this.chip.querySelector('[data-looks-chip-box]');
      var text = this.chip.querySelector('[data-looks-chip-text]');
      var gen = this.generating();
      var rec = this.chipLook ? this.find(this.chipLook) : null;
      var state = '';
      var target = null;
      // a finished look the person already sees in the stage needs no chip: one entry to the viewer per look (N2 of the re-validation)
      if (!gen.length && rec && rec.status !== 'generating' && this.stageSeen && !this.modalOpen) {
        var shown = this.stageLook();
        if (shown && shown.id === rec.id) this.dismissed[rec.id] = true;
      }
      if (gen.length) { state = 'generating'; target = gen[0]; }
      else if (rec && rec.status !== 'generating' && !this.dismissed[rec.id] && !(this.modalOpen && this.selected === rec.id)) { state = rec.status === 'error' ? 'error' : 'ready'; target = rec; }
      if (state === 'generating' && this.dismissed['generating']) state = '';
      if (!box.hidden !== !!state) box.hidden = !state;
      if (this.chip.getAttribute('data-state') !== (state || 'idle')) this.chip.setAttribute('data-state', state || 'idle');
      if (!state) return;
      // while creating, the chip carries the live time copy of the stage (N1), not a second "Creando tu look…"
      var copy = state === 'generating' ? this.statusText(target) : state === 'error' ? this.tx('chip', 2) : this.tx('chip', 1);
      if (text.textContent !== copy) text.textContent = copy;
      this.chip.setAttribute('data-look', target.id);
      var mail = this.chip.querySelector('[data-looks-chip-email]');
      if (mail) {
        var mailRec = this.find(target.id);
        mail.hidden = state !== 'generating' || !this.emailOffered || !mailRec || !mailRec.renderId;
        mail.setAttribute('aria-pressed', this.emailOn && this.emailOn[target.id] ? 'true' : 'false');
        mail.setAttribute('aria-label', this.emailOn && this.emailOn[target.id] ? this.tx('chip', 12) : this.tx('chip', 11));
      }
      var bell = this.chip.querySelector('[data-looks-chip-notify]');
      if (bell) {
        var can = this.notifySupported() && !(Notification.permission === 'denied' && !this.notifyOptIn);
        bell.hidden = state !== 'generating' || !can;
        bell.setAttribute('aria-pressed', this.notifyOn() ? 'true' : 'false');
        bell.setAttribute('aria-label', this.notifyOn() ? this.tx('chip', 7) : this.tx('chip', 6));
      }
      this.syncChipLift();
    }

    // the chip rides above the sticky "Crear mi look" bar of the fitting room when that bar is on screen
    syncChipLift() {
      if (!this.chip) return;
      var room = this.closest('nc-fitting-room');
      var dock = room && room.querySelector('[data-fr-dock]');
      var on = !!(dock && !dock.hidden && dock.getClientRects().length);
      this.chip.classList.toggle('nc-looks-chip--lifted', on);
    }

    onChipClick(event) {
      var close = event.target.closest('[data-looks-chip-close]');
      if (close) {
        var st = this.chip.getAttribute('data-state');
        if (st === 'generating') this.dismissed['generating'] = true; else this.dismissed[this.chip.getAttribute('data-look')] = true;
        this.renderChip();
        return;
      }
      if (event.target.closest('[data-looks-chip-notify]')) { this.toggleNotify(); return; }
      if (event.target.closest('[data-looks-chip-email]')) { this.toggleEmail(); return; }
      if (!event.target.closest('[data-looks-chip-open]')) return;
      var id = this.chip.getAttribute('data-look');
      // creating, below the two-column desktop: the tap brings the look's card of "Mis looks" into view (the stage already shows the wait)
      var card = this.chip.getAttribute('data-state') === 'generating' && !this.isInline() ? this.cardButton(id) : null;
      if (!card) { this.open(id); return; }
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      card.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
      card.focus({ preventScroll: true });
    }

    // ---- interactions ----
    onClick(event) {
      var t = event.target;
      var retry = t.closest('[data-looks-retry]');
      if (retry) { this.retry(retry.getAttribute('data-looks-retry')); return; }
      // N11: the ONE action of the empty gallery; the host (nc-fitting-room) brings the person to its catalogue
      if (t.closest('[data-looks-first]')) { this.emit('first', {}); return; }
      var open = t.closest('[data-looks-open]');
      if (open) this.open(open.getAttribute('data-looks-open'), open);
    }

    retry(id) {
      var rec = this.find(id);
      if (!rec) return;
      this.emit('retry', { id: id, productIds: rec.items.map(function (i) { return i.productId; }) });
    }

    onViewerClick(event) {
      var t = event.target;
      var vbtn = t.closest('[data-lv-view]');
      if (vbtn) { this.showBack(vbtn.getAttribute('data-lv-view') === 'back'); return; }
      var zb = t.closest('[data-lv-zoom]');
      if (zb) { var kind = zb.getAttribute('data-lv-zoom'); if (kind === 'reset') this.resetZoom(); else this.zoomBy(kind === 'in' ? ZOOM_STEP : 1 / ZOOM_STEP); this.sayZoom(); return; }
      var go = t.closest('[data-lv-go]');
      if (go) { this.selected = go.getAttribute('data-lv-go'); this.renderViewer(); go = this.viewer.querySelector('[data-lv-go="' + this.selected + '"]'); if (go) go.focus(); return; }
      var stepBtn = t.closest('[data-lv-step]');
      if (stepBtn) { if (stepBtn.getAttribute('aria-disabled') !== 'true') this.step(parseInt(stepBtn.getAttribute('data-lv-step'), 10)); return; }
      if (t.closest('[data-lv-retry]')) { this.retry(this.selected); return; }
      if (t.closest('[data-lv-edit]')) { this.edit(); return; }
      var save = t.closest('[data-lv-save]');
      if (save && !save.disabled) { this.toggleSaved(save); return; }
      if (t.closest('[data-lv-compare]')) { this.openCompare(t.closest('[data-lv-compare]')); return; }
      if (t.closest('[data-lv-share]')) { if (t.closest('[data-lv-share]').disabled) return; this.emit('share', { id: this.selected }); this.shareLook(this.selected); return; }
    }

    onViewerKey(event) {
      var tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // zoom from the keyboard: + / − / 0, and the arrows pan a zoomed look (they page the looks otherwise)
      if (this.zoomReady() && (event.key === '+' || event.key === '=' || event.key === '-' || event.key === '_' || (event.key === '0' && this.zoom.s > 1))) {
        event.preventDefault();
        if (event.key === '0') this.resetZoom(); else this.zoomBy(event.key === '-' || event.key === '_' ? 1 / ZOOM_STEP : ZOOM_STEP);
        this.sayZoom();
        return;
      }
      var pan = { ArrowUp: [0, 40], ArrowDown: [0, -40], ArrowLeft: [40, 0], ArrowRight: [-40, 0] }[event.key];
      if (pan && this.zoom.s > 1) { event.preventDefault(); this.setZoom({ s: this.zoom.s, x: this.zoom.x + pan[0], y: this.zoom.y + pan[1] }); return; }
      var map = { ArrowUp: -1, ArrowDown: 1 };
      if (map[event.key]) { event.preventDefault(); this.step(map[event.key]); return; }
      if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.step(event.key === 'Home' ? -999 : 999); }
    }

    // ---- zoom on the generated image (2026-10-30): pinch, double tap / double click, drag, Ctrl + wheel, the − + buttons and the keys ----
    zoomBox() {
      var m = this.viewer.querySelector('[data-lv-media]');
      var r = m ? m.getBoundingClientRect() : { width: 0, height: 0, left: 0, top: 0 };
      return { w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    }
    zoomReady() { var m = this.viewer && this.viewer.querySelector('[data-lv-media]'); return !!(m && m.getAttribute('data-zoom-ready') === 'true'); }
    // paints one state: the transform on the picture, the cursor / live flags on the frame and the − + buttons; `live` = mid-gesture
    setZoom(z, live) {
      var m = this.viewer && this.viewer.querySelector('[data-lv-media]');
      if (!m) return;
      this.zoom = zoomClamp(z, this.zoomBox());
      var img = m.querySelector('[data-lv-img] > img');
      if (img) img.style.transform = zoomCss(this.zoom);
      var on = this.zoom.s > 1;
      m.setAttribute('data-zoom', on ? 'on' : 'off');
      if (live) m.setAttribute('data-zoom-live', ''); else m.removeAttribute('data-zoom-live');
      var bar = m.querySelector('[data-lv-zoom-bar]');
      if (bar) bar.hidden = !this.zoomReady();
      var inB = m.querySelector('[data-lv-zoom="in"]'), outB = m.querySelector('[data-lv-zoom="out"]'), reset = m.querySelector('[data-lv-zoom="reset"]');
      if (inB) inB.disabled = this.zoom.s >= ZOOM_MAX;
      if (outB) outB.disabled = !on;
      if (reset) reset.hidden = !on;
      if (on && this.selected && !this.zoomedOnce[this.selected]) { this.zoomedOnce[this.selected] = true; this.emit('zoom', { id: this.selected, scale: this.zoom.s }); }
    }
    resetZoom() { if (this.zoom.s !== 1 || this.zoom.x || this.zoom.y) this.setZoom({ s: 1, x: 0, y: 0 }); }
    zoomBy(k, px, py) { if (this.zoomReady()) this.setZoom(zoomAt(this.zoom, k, px || 0, py || 0, this.zoomBox())); }
    sayZoom() { this.announce(fillText(this.tx('viewer', 23), { n: Math.round(this.zoom.s * 100) })); }
    bindZoom() {
      var self = this;
      var m = this.viewer.querySelector('[data-lv-media]');
      if (!m || typeof window === 'undefined' || !window.PointerEvent) return;
      var pts = {}, count = 0, pinch = null, drag = null, lastTap = 0, moved = false;
      var rel = function (e) { var b = self.zoomBox(); return { x: e.clientX - b.cx, y: e.clientY - b.cy }; };
      var pair = function () { var k = Object.keys(pts); return [pts[k[0]], pts[k[1]]]; };
      var dist = function () { var p = pair(); return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1; };
      var mid = function () { var p = pair(); return { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 }; };
      m.addEventListener('pointerdown', function (e) {
        if (!self.zoomReady() || e.target.closest('button, a')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pts[e.pointerId] = rel(e); count = Object.keys(pts).length; moved = false;
        try { m.setPointerCapture(e.pointerId); } catch (err) { /* older engines */ }
        if (count === 2) { pinch = { d: dist(), z: self.zoom }; drag = null; }
        else if (count === 1 && self.zoom.s > 1) { drag = { x: e.clientX, y: e.clientY, z: self.zoom }; if (e.pointerType === 'mouse') e.preventDefault(); }
      });
      m.addEventListener('pointermove', function (e) {
        if (!pts[e.pointerId]) return;
        pts[e.pointerId] = rel(e);
        if (count === 2 && pinch) {
          var c = mid();
          moved = true;
          self.setZoom(zoomAt(pinch.z, dist() / pinch.d, c.x, c.y, self.zoomBox()), true);
        } else if (count === 1 && drag) {
          var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
          if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
          self.setZoom({ s: drag.z.s, x: drag.z.x + dx, y: drag.z.y + dy }, true);
        }
      });
      var end = function (e) {
        if (!pts[e.pointerId]) return;
        var p = pts[e.pointerId];
        delete pts[e.pointerId]; count = Object.keys(pts).length;
        if (count < 2 && pinch) { pinch = null; self.setZoom(self.zoom); }
        if (count === 0) {
          if (drag) { drag = null; self.setZoom(self.zoom); }
          // double tap (touch / pen) toggles the zoom about the tapped point; the mouse has dblclick
          if (e.type === 'pointerup' && e.pointerType !== 'mouse' && !moved) {
            if (e.timeStamp - lastTap < 320) { lastTap = 0; self.setZoom(zoomToggle(self.zoom, p.x, p.y, self.zoomBox())); self.sayZoom(); }
            else lastTap = e.timeStamp;
          }
        }
        try { m.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      };
      m.addEventListener('pointerup', end);
      m.addEventListener('pointercancel', end);
      m.addEventListener('dblclick', function (e) {
        if (!self.zoomReady() || e.target.closest('button, a')) return;
        e.preventDefault();
        var p = rel(e);
        self.setZoom(zoomToggle(self.zoom, p.x, p.y, self.zoomBox()));
        self.sayZoom();
      });
      // a trackpad pinch and Ctrl / ⌘ + wheel zoom about the cursor; a plain wheel keeps scrolling the page
      m.addEventListener('wheel', function (e) {
        if (!self.zoomReady() || !(e.ctrlKey || e.metaKey)) return;
        e.preventDefault();
        var p = rel(e);
        self.setZoom(zoomAt(self.zoom, Math.exp(-e.deltaY * 0.01), p.x, p.y, self.zoomBox()));
      }, { passive: false });
    }

    edit() {
      var rec = this.find(this.selected);
      if (!rec) return;
      var detail = { id: rec.id, productIds: rec.items.map(function (i) { return i.productId; }), title: fillText(this.tx('looks', 2), { n: lookNumber(this.list, rec.id) }) };
      var self = this;
      if (this.modalOpen && this.sheet) {
        // the host moves the focus to the builder once the sheet has closed
        var once = function () { self.sheetWrap.removeEventListener('nc-sheet:close', once); self.emit('edit', detail); };
        this.sheetWrap.addEventListener('nc-sheet:close', once);
        this.sheet.close();
        return;
      }
      this.emit('edit', detail);
    }

    // Guardar = the one heart (CLAUDE.md §10): the generated look is kept (saved: true) through the gateway; a refusal puts it back and says so
    toggleSaved(btn) {
      var rec = this.find(this.selected);
      if (!rec || !this.gw || typeof this.gw.saveGeneratedLook !== 'function') return;
      var self = this;
      var next = !rec.saved;
      rec.saved = next;
      btn.setAttribute('aria-pressed', next ? 'true' : 'false');
      btn.setAttribute('data-state', 'loading');
      btn.setAttribute('aria-busy', 'true');
      this.gw.saveGeneratedLook(rec).then(function () {
        btn.removeAttribute('data-state');
        btn.removeAttribute('aria-busy');
        self.renderViewer();
        self.announce(next ? self.tx('viewer', 3) : self.tx('viewer', 2));
      }, function () {
        rec.saved = !next;
        btn.removeAttribute('aria-busy');
        btn.setAttribute('data-state', 'error');
        btn.setAttribute('aria-pressed', rec.saved ? 'true' : 'false');
        if (window.NcToast) window.NcToast.show({ tone: 'error', message: self.tx('viewer', 18) });
        setTimeout(function () { if (btn.getAttribute('data-state') === 'error') btn.removeAttribute('data-state'); }, 3000);
      });
    }
  }

  customElements.define('nc-looks', NcLooksElement);
})();
